import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { connect, StringCodec, NatsConnection, Subscription } from 'nats.ws';
import { HttpClient, HttpHeaders } from '@angular/common/http';

export interface NatsConfig {
  url: string;
  env: string;
  clientId: string;
  orgId: string;
  subjects?: {
    userNotifications?: string;
    systemGlobal?: string;
  };
  urlDomain?: string;
  urlTcp?: string;
  urlDomainTcp?: string;
  urlWss?: string;
  urlDomainWss?: string;
  auth?: {
    username?: string;
    password?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  public isConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public connectionError: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private nc: NatsConnection | null = null;
  private sc = StringCodec();
  private env = 'local';
  private orgId = '';
  private subscriptions: Map<string, Subscription> = new Map();
  private callbacks: Map<string, Array<(msg: any) => void>> = new Map();
  private config: NatsConfig | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private isReconnecting = false;
  /** Set when disconnect() is called (e.g. logout); prevents closed() from scheduling reconnect. */
  private intentionalDisconnect = false;
  private initPromise: Promise<void> | null = null;
  private initState: 'not_started' | 'succeeded' | 'failed' = 'not_started';
  private initError: unknown = null;
  private reconnectPromise: Promise<void> | null = null;

  constructor(private http: HttpClient) {
    this.env = environment.environment === 'local' ? 'staging' : environment.environment;
    this.orgId = this.getOrgIdFromStorage() || environment.orgId;
  }

  private debugLog(message: string, data?: any): void {
    if (environment.environment == 'local') {
      return;
    }
    if (data !== undefined) {
      console.log(`[MessagingService] ${message}`, data);
      return;
    }
    console.log(`[MessagingService] ${message}`);
  }

  private getOrgIdFromStorage(): string {
    try {
      const selectedOrg = localStorage.getItem('selectedOrg');
      if (selectedOrg) return JSON.parse(selectedOrg);
    } catch { }
    try {
      const o = localStorage.getItem('o');
      if (o) return JSON.parse(o);
    } catch { }
    return '';
  }

  private browserNatsEnabled(): boolean {
    return (environment as any).enableBrowserNats !== false;
  }

  public getUserLive(callback: (dataResponse: any[] | undefined, requestError: any | undefined) => void): void;
  public getUserLive(userIds: string[], callback: (dataResponse: any[] | undefined, requestError: any | undefined) => void): void;
  public getUserLive(
    userIdsOrCallback: string[] | ((dataResponse: any[] | undefined, requestError: any | undefined) => void),
    callbackMaybe?: (dataResponse: any[] | undefined, requestError: any | undefined) => void
  ) {
    const userIds = Array.isArray(userIdsOrCallback) ? userIdsOrCallback : [];
    const callback = (Array.isArray(userIdsOrCallback) ? callbackMaybe : userIdsOrCallback) as
      ((dataResponse: any[] | undefined, requestError: any | undefined) => void);

    let tokenRaw = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    let token = tokenRaw;
    try {
      const parsed = JSON.parse(tokenRaw);
      if (parsed && parsed.token) {
        token = parsed.token;
      }
    } catch { }

    const headers = new HttpHeaders({
      'Authorization': token,
      'organizationid': this.getOrgIdFromStorage() || this.orgId || environment.orgId,
      'productid': environment.productId || '',
      'project': environment.projectName || 'cipher'
    });

    this.http.post<any>(`${environment.serverUrl}user/live`, userIds, { headers }).subscribe(
      (response) => {
        if (response?.status && response?.results) {
          callback(response.results, undefined);
          return;
        }
        callback(undefined, response?.message || 'Data error from server');
      },
      (error) => {
        callback(undefined, error);
      }
    );
  }

  public setOrgId(orgId: string) {
    if (orgId && orgId !== this.orgId) {
      this.orgId = orgId;
    }
  }

  /**
   * Fetch NATS configuration from backend API
   */
  async fetchConfig(): Promise<NatsConfig> {
    // Get token from storage - it might be stored as JSON object or plain string
    let tokenRaw = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    let token = tokenRaw;

    // If token is stored as JSON object, extract the actual token
    try {
      const parsed = JSON.parse(tokenRaw);
      if (parsed && parsed.token) {
        token = parsed.token;
      }
    } catch {
      // Not JSON, use as-is
    }

    const headers = new HttpHeaders({
      'Authorization': token,
      'organizationid': this.getOrgIdFromStorage() || this.orgId || environment.orgId,
      'productid': environment.productId || '',
      'project': environment.projectName || 'cipher'
    });

    try {
      const response = await this.http.get<any>(
        `${environment.serverUrl}user/bus/auth`,
        { headers }
      ).toPromise();

      // Handle response structure from backend controller
      // Controller returns: { status: true, results: { url, env, clientId, orgId, subjects } }
      let configData: any = null;

      if (response?.status && response?.results) {
        // Standard wrapped response: { status: true, results: { url, env, ... } }
        configData = response.results;
      } else if (response?.config) {
        // Direct config response (legacy)
        configData = response.config;
      } else if (response?.url || response?.urlDomainWss) {
        // Direct response without wrapper
        configData = response;
      }

      if (configData && (configData.url || configData.urlDomainWss)) {
        this.config = configData as NatsConfig;
        this.env = this.config.env || this.env;
        this.orgId = this.config.orgId || this.orgId;
        // console.log('NATS config loaded:', this.config);
        return this.config;
      }

      // console.warn('NATS config response missing url, using fallback. Response was:', response);
      throw new Error('Invalid config response - missing url');
    } catch (error: any) {
      console.error('Failed to fetch NATS config:', error);
      // Fallback to environment config
      return {
        url: environment.natsWsUrl || '',
        env: this.env,
        clientId: '',
        orgId: this.orgId
      };
    }
  }

  private setConnected(connected: boolean): void {
    if (this.isConnected.value !== connected) {
      this.debugLog(`isConnected -> ${connected}`);
      this.isConnected.next(connected);
    }
  }

  private setConnectionError(errorMessage: string | null): void {
    if (this.connectionError.value !== errorMessage) {
      this.debugLog(`connectionError -> ${errorMessage}`);
      this.connectionError.next(errorMessage);
    }
  }

  protected async natsConnect(options: any): Promise<NatsConnection> {
    return connect(options);
  }

  async init(): Promise<void> {
    this.debugLog(`init() called. state=${this.initState} nc=${!!this.nc} inFlight=${!!this.initPromise}`);
    if (!this.browserNatsEnabled()) {
      this.setConnected(false);
      this.setConnectionError('Browser NATS is disabled for this environment');
      return;
    }
    if (this.initState === 'succeeded') {
      this.debugLog('init() short-circuit: already succeeded');
      return;
    }
    if (this.initState === 'failed') {
      this.debugLog('init() short-circuit: cached failure', this.initError);
      return Promise.reject(this.initError || new Error('NATS initialization previously failed'));
    }
    if (this.initPromise) {
      this.debugLog('init() queued: awaiting in-flight init promise');
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        this.debugLog('init() starting establishConnection()');
        await this.establishConnection();
        this.initState = 'succeeded';
        this.initError = null;
        this.debugLog('init() succeeded');
      } catch (error) {
        this.initState = 'failed';
        this.initError = error;
        this.debugLog('init() failed', error);
        throw error;
      } finally {
        this.initPromise = null;
        this.debugLog(`init() finished. state=${this.initState}`);
      }
    })();

    return this.initPromise;
  }

  async resetInitialization(): Promise<void> {
    this.debugLog('resetInitialization() called');
    await this.disconnect();
    this.initPromise = null;
    this.initState = 'not_started';
    this.initError = null;
    this.reconnectPromise = null;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.intentionalDisconnect = false;
    this.setConnectionError(null);
    this.debugLog('resetInitialization() completed');
  }

  private async establishConnection(): Promise<void> {
    if (this.nc) {
      this.debugLog('establishConnection() short-circuit: nc already exists');
      return;
    }

    this.debugLog('establishConnection() fetching config');
    const config = await this.fetchConfig();
    const url = config.urlDomainWss || config.url || environment.natsWsUrl;

    if (!url) {
      const e = new Error('Missing NATS WebSocket URL');
      this.setConnectionError(e.message);
      this.setConnected(false);
      this.debugLog('establishConnection() failed: missing url');
      throw e;
    }

    try {
      this.debugLog('establishConnection() connecting', { url, reconnectDelay: this.reconnectDelay });
      if (!url.startsWith('wss://') && url.startsWith('ws://')) {
        const e = new Error('NATS WebSocket URL is not secure (WSS required)');
        this.setConnectionError(e.message);
        throw e;
      }
      const connectOptions: any = {
        servers: url,
        reconnect: true,
        maxReconnectAttempts: -1,
        reconnectTimeWait: this.reconnectDelay,
        pingInterval: 30000,
        timeout: 10000
      };
      if ((config as any).clientJwt) {
        connectOptions.token = (config as any).clientJwt;
      }
      if (config.auth?.username && config.auth?.password) {
        connectOptions.user = config.auth.username;
        connectOptions.pass = config.auth.password;
      }
      this.nc = await this.natsConnect(connectOptions);

      this.debugLog('establishConnection() connected');
      this.setConnected(true);
      this.setConnectionError(null);
      this.reconnectAttempts = 0;

      const testSubject = `self.test.${Date.now()}`;
      this.debugLog('establishConnection() self-test subscribe/publish', { testSubject });
      const sub = await this.nc.subscribe(testSubject);
      (async () => {
        for await (const m of sub) {
          console.log(`[MessagingService] SELF-TEST PASSED: Received message on ${testSubject}`);
        }
      })();
      await this.nc.publish(testSubject, this.sc.encode(JSON.stringify({ test: true })));

      this.debugLog('establishConnection() registered connection close handler');
      this.handleConnectionEvents();
    } catch (error: any) {
      console.error('NATS connection error:', error);
      this.setConnectionError(error?.message || 'NATS connection error');
      this.setConnected(false);
      this.nc = null;
      this.debugLog('establishConnection() threw error', error);
      throw error;
    }
  }

  private handleConnectionEvents(): void {
    if (!this.nc) return;

    // Monitor for connection close
    this.nc.closed().then(() => {
      console.warn('NATS connection closed');
      this.debugLog(`closed() resolved. intentionalDisconnect=${this.intentionalDisconnect} isReconnecting=${this.isReconnecting} state=${this.initState}`);
      this.setConnected(false);
      this.nc = null;
      this.subscriptions.clear();

      // Do not reconnect when user logged out or disconnect() was called intentionally
      if (this.intentionalDisconnect) {
        this.intentionalDisconnect = false;
        this.debugLog('closed() exit: intentional disconnect');
        return;
      }
      if (!this.isReconnecting) {
        this.scheduleReconnect();
      }
    });
  }

  private scheduleReconnect(): void {
    if (!this.browserNatsEnabled()) {
      return;
    }
    if (this.initState !== 'succeeded') {
      this.debugLog(`scheduleReconnect() skipped: initState=${this.initState}`);
      return;
    }
    if (this.isReconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.debugLog(`scheduleReconnect() skipped: isReconnecting=${this.isReconnecting} attempts=${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
      return;
    }

    this.isReconnecting = true;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`Scheduling NATS reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    setTimeout(async () => {
      this.reconnectAttempts++;
      this.isReconnecting = false;

      try {
        this.debugLog(`reconnect attempt ${this.reconnectAttempts} starting`);
        await this.reconnect();
        this.debugLog(`reconnect attempt ${this.reconnectAttempts} succeeded`);
      } catch {
        this.debugLog(`reconnect attempt ${this.reconnectAttempts} failed; scheduling again`);
        this.scheduleReconnect();
      }
    }, delay);
  }

  private async reconnect(): Promise<void> {
    if (this.reconnectPromise) {
      this.debugLog('reconnect() queued: awaiting in-flight reconnect promise');
      return this.reconnectPromise;
    }

    this.reconnectPromise = (async () => {
      await this.establishConnection();
      await this.resubscribeAll();
    })().finally(() => {
      this.reconnectPromise = null;
    });

    return this.reconnectPromise;
  }

  private async resubscribeAll(): Promise<void> {
    if (!this.nc) return;

    const subjects = Array.from(this.callbacks.keys());
    this.subscriptions.clear();
    for (const subject of subjects) {
      await this.ensureSubscribed(subject);
    }
  }

  private async ensureSubscribed(subject: string): Promise<void> {
    if (!this.nc) return;
    if (this.subscriptions.has(subject)) return;

    const sub = await this.nc.subscribe(subject);
    this.subscriptions.set(subject, sub);
    (async () => {
      for await (const m of sub) {
        try {
          const decoded = this.sc.decode(m.data);
          const json = JSON.parse(decoded);
          const subjectCallbacks = this.callbacks.get(subject) || [];
          for (const cb of subjectCallbacks) {
            try {
              cb(json);
            } catch (cbError) {
              console.error(`[MessagingService] Callback error:`, cbError);
            }
          }
        } catch (e) {
          console.error(`[MessagingService] Error parsing message:`, e);
        }
      }
    })();
  }

  private subjectFor(channelName: string, messageName?: string): string {
    if (messageName && messageName.startsWith('case-')) {
      const caseId = messageName.replace('case-', '');
      if (channelName === 'note') {
        return `${this.env}.${this.orgId}.note.case.${caseId}`;
      }
      if (channelName === 'case' || channelName === 'material') {
        return `${this.env}.${this.orgId}.case.${caseId}`;
      }
      if (channelName === 'live-pins') {
        return `${this.env}.${this.orgId}.${channelName}.case.${caseId}`;
      }
    } else if (messageName && messageName.startsWith('prefecture-')) {
      const prefecture = messageName.replace('prefecture-', '');
      return `${this.env}.${this.orgId}.${prefecture}.${channelName}`;
    }
    return `${this.env}.${this.orgId}.${channelName}`;
  }

  async publish(messageName: string, channelName: string, payload: any): Promise<void> {
    if (!this.nc) await this.init();
    if (!this.nc) return;
    const subject = this.subjectFor(channelName, messageName);
    // console.log('Publishing message', { subject, messageName, channelName, payload });
    const data = JSON.stringify({ type: messageName, data: payload });
    await this.nc.publish(subject, this.sc.encode(data));
  }

  async subscribe(messageName: string, channelName: string, callback: (msg: any) => void): Promise<void> {
    if (!this.nc) await this.init();
    if (!this.nc) return;

    // REVERTED: Always use subjectFor to ensure env/orgId prefixes are applied
    // The google-map component passes the *suffix* (e.g. 'live.users.123'), not the full subject.
    const subject = this.subjectFor(channelName, messageName);
    // console.log(`[MessagingService] Subscribing to ${subject}. Callback added.`);

    // Add callback to the list for this subject
    if (!this.callbacks.has(subject)) {
      this.callbacks.set(subject, []);
    }
    this.callbacks.get(subject)!.push(callback);
    // console.log(`[MessagingService] Callbacks count for ${subject}: ${this.callbacks.get(subject)!.length}`);

    // If already subscribed, just add the callback (don't create new subscription)
    if (this.subscriptions.has(subject)) {
      // console.log(`[MessagingService] Already subscribed to ${subject}.`);
      return;
    }
    await this.ensureSubscribed(subject);
  }

  async unsubscribe(messageName: string, channelName: string, callback?: (msg: any) => void): Promise<void> {
    const subject = this.subjectFor(channelName, messageName);
    // console.log(`[MessagingService] Unsubscribing from ${subject}. Callback provided: ${!!callback}`);

    if (callback) {
      const callbacks = this.callbacks.get(subject);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
          // console.log(`[MessagingService] Callback removed. Remaining: ${callbacks.length}`);
        } else {
          console.warn(`[MessagingService] Callback not found for ${subject}`);
        }
        if (callbacks.length === 0) {
          this.callbacks.delete(subject);
          // Drain subscription if no callbacks left
          const sub = this.subscriptions.get(subject);
          if (sub) {
            // console.log(`[MessagingService] Draining subscription for ${subject}`);
            await sub.drain();
            this.subscriptions.delete(subject);
          }
        }
      }
    } else {
      // console.log(`[MessagingService] Unsubscribing ALL for ${subject}`);
      const sub = this.subscriptions.get(subject);
      if (sub) {
        await sub.drain();
        this.subscriptions.delete(subject);
      }
      // Clear callbacks to prevent duplicate message handling on re-subscribe
      this.callbacks.delete(subject);
    }
  }

  async disconnect(): Promise<void> {
    this.debugLog('disconnect() called');
    this.intentionalDisconnect = true;
    const subs = Array.from(this.subscriptions.values());
    this.subscriptions.clear();
    this.callbacks.clear(); // Clear all callbacks to prevent duplicates on reconnect
    for (const s of subs) {
      try { await s.drain(); } catch { }
    }
    if (this.nc) {
      try { await this.nc.close(); } catch { }
      this.nc = null;
      this.setConnected(false);
    }
    this.initPromise = null;
    this.initState = 'not_started';
    this.initError = null;
    this.reconnectPromise = null;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.debugLog('disconnect() completed');
  }
}
